import React, { useState, useEffect, useRef } from 'react';
import { dbRealtime } from '../firebase';
import { ref, get as dbGet, set as dbSet } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeKey, matchesExcelWildcard, classifyDescription } from '../utils/helpers';
import { invalidateCache } from '../utils/mappingCache';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  return lines.map(line => {
    const match = line.match(/^(?:"([^"]*)"|([^,]*)),(?:"([^"]*)"|([^,]*))$/);
    if (match) {
      return [match[1] || match[2] || '', match[3] || match[4] || ''];
    } else {
      const idx = line.indexOf(',');
      if (idx === -1) return [line, ''];
      return [line.slice(0, idx), line.slice(idx + 1)];
    }
  });
}

function saveToLocalStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}
function loadFromLocalStorage(key, fallback) {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

function analyzeLocalMappings(mappings) {
  // Similar to Database.js, but for local data
  const results = [];
  let duplicatesRemoved = 0;
  // Remove duplicates (same keyword, same GL account)
  const duplicateGroups = {};
  mappings.forEach(mapping => {
    const key = `${mapping.description.toLowerCase()}_${mapping.glAccount}`;
    if (!duplicateGroups[key]) duplicateGroups[key] = [];
    duplicateGroups[key].push(mapping);
  });
  const uniqueMappings = [];
  Object.keys(duplicateGroups).forEach(key => {
    const group = duplicateGroups[key];
    if (group.length > 1) {
      duplicatesRemoved += group.length - 1;
    }
    uniqueMappings.push(group[0]);
  });
  // Find consolidation opportunities (same GL account, similar keywords)
  const groups = uniqueMappings.reduce((acc, mapping) => {
    const { glAccount, description } = mapping;
    if (!acc[glAccount]) acc[glAccount] = [];
    acc[glAccount].push(description);
    return acc;
  }, {});
  for (const glAccount in groups) {
    // Normalize: remove special chars, lowercase
    const descriptions = groups[glAccount].map(d => d.toLowerCase().replace(/[^a-z0-9 ]/g, ''));
    const descCovered = new Array(descriptions.length).fill(false);
    const allSubstrings = {};
    // Generate all substrings for all descriptions
    for (let idx = 0; idx < descriptions.length; idx++) {
      const str = descriptions[idx];
      if (str.length < 100) {
        for (let i = 0; i < str.length; i++) {
          for (let j = i + 6; j <= str.length; j++) {
            const sub = str.substring(i, j);
            if (sub.trim().length < 5) continue;
            if (!allSubstrings[sub]) allSubstrings[sub] = new Set();
            allSubstrings[sub].add(idx);
          }
        }
      }
    }
    // Remove substrings that conflict with other GL accounts
    const otherMappings = uniqueMappings.filter(m => m.glAccount !== glAccount);
    const otherDescriptions = otherMappings.map(m => m.description.toLowerCase().replace(/[^a-z0-9 ]/g, ''));
    const nonConflicting = Object.entries(allSubstrings).filter(([sub]) =>
      !otherDescriptions.some(desc => desc.includes(sub))
    );
    // Greedy set cover: iteratively pick the substring that covers the most uncovered descriptions
    const selectedKeywords = [];
    let uncovered = descCovered.map((_, i) => i);
    while (uncovered.length > 0) {
      // Find the substring that covers the most uncovered descriptions
      let best = null;
      let bestCoverage = [];
      for (const [sub, idxSet] of nonConflicting) {
        const coverage = uncovered.filter(i => idxSet.has(i));
        if (
          coverage.length > (bestCoverage.length || 0) ||
          (coverage.length === (bestCoverage.length || 0) && sub.length > (best ? best.length : 0))
        ) {
          best = sub;
          bestCoverage = coverage;
        }
      }
      if (!best || bestCoverage.length < 2) break; // Only keep if covers at least 2
      // Check if this keyword covers more than 80% of descriptions
      const coveragePercent = bestCoverage.length / descriptions.length;
      let status = 'OK';
      let conflict = null;
      if (coveragePercent >= 0.8) {
        // Check for conflict: is this keyword found in another GL account?
        for (const mapping of uniqueMappings) {
          if (
            mapping.description.toLowerCase().replace(/[^a-z0-9 ]/g, '').includes(best) &&
            mapping.glAccount !== glAccount
          ) {
            status = 'CONFLICT';
            conflict = mapping;
            break;
          }
        }
      }
      selectedKeywords.push({
        keyword: best.toUpperCase(),
        glAccount,
        count: bestCoverage.length,
        originalCount: descriptions.length,
        status,
        conflict,
      });
      // Mark covered
      uncovered = uncovered.filter(i => !bestCoverage.includes(i));
    }
    // Add results
    for (const kw of selectedKeywords) {
      results.push(kw);
    }
  }
  return { results, uniqueMappings, duplicatesRemoved };
}

function mappingsToCSV(mappings) {
  return mappings.map(m => `"${m.description.replace(/"/g, '""')}" , "${m.glAccount.replace(/"/g, '""')}"`).join('\n');
}


function UploadData() {
  const { user, userDoc } = useAuth();
  const [pastedData, setPastedData] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [glAccountMapping, setGlAccountMapping] = useState({});
  const [localMappings, setLocalMappings] = useState(loadFromLocalStorage('localMappings', []));
  const [analysis, setAnalysis] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    saveToLocalStorage('localMappings', localMappings);
  }, [localMappings]);

  const classifyDescriptionForCheck = (description) => {
    return classifyDescription(description, glAccountMapping) === 'No Match' ? null : classifyDescription(description, glAccountMapping);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = parseCSV(text);
      const mappings = rows.map(row => ({ description: row[0] || '', glAccount: row[1] || '' }));
      setLocalMappings(mappings);
      setPastedData(rows.map(row => `${row[0] || ''}\t${row[1] || ''}`).join('\n'));
    };
    reader.readAsText(file);
  };

  const handlePaste = (e) => {
    setPastedData(e.target.value);
    const lines = e.target.value.split(/\r?\n/).filter(Boolean);
    const mappings = [];
    for (const line of lines) {
      const parts = line.split('\t');
      if (parts.length >= 2) {
        const description = parts[0].trim();
        const glAccount = parts[1].trim();
        if (description && glAccount && description.trim().length >= 5) {
          mappings.push({ description, glAccount });
        }
      }
    }
    setLocalMappings(mappings);
  };

  const handleConsolidate = () => {
    if (!analysis) return;
    // Remove duplicates, keep only consolidated keywords
    let newMappings = [...analysis.uniqueMappings];
    for (const result of analysis.results) {
      if (result.status === 'OK') {
        // Remove all mappings for this GL account
        newMappings = newMappings.filter(m => m.glAccount !== result.glAccount);
        // Add the consolidated keyword
        newMappings.push({ description: result.keyword, glAccount: result.glAccount });
      }
    }
    setConsolidated(newMappings);
    setMessage('Consolidation complete! You can now download as CSV or add to Firebase.');
  };

  const handleDownloadCSV = () => {
    const data = consolidated || localMappings;
    const csv = mappingsToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'consolidated_mappings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadToFirebase = async () => {
    const data = consolidated || localMappings;
    setIsUploading(true);
    setMessage('Checking for conflicts...');
    try {
      // Fetch current mappings
      const snapshot = await dbGet(ref(dbRealtime, 'gl_mappings'));
      const current = snapshot.exists() ? snapshot.val() : {};

      const updates = { ...current };
      const conflictsList = [];
      let addedCount = 0;
      let skippedCount = 0;

      for (let i = 0; i < data.length; i++) {
        const mapping = data[i];
        const sanitizedKey = sanitizeKey(mapping.description);

        if (!current[sanitizedKey]) {
          // New mapping, add it
          updates[sanitizedKey] = {
            description: mapping.description,
            glAccount: mapping.glAccount
          };
          addedCount++;
        } else {
          // Check if there's a conflict (same keyword but different GL Account)
          const existing = current[sanitizedKey];
          const existingGlAccount = typeof existing === 'string' ? existing : existing.glAccount;

          if (existingGlAccount !== mapping.glAccount) {
            // Conflict detected
            conflictsList.push({
              keyword: mapping.description,
              sanitizedKey: sanitizedKey,
              existingGlAccount: existingGlAccount,
              newGlAccount: mapping.glAccount,
              index: i
            });
          } else {
            // Same keyword and same GL Account, skip
            skippedCount++;
          }
        }

        setProgress(((i + 1) / data.length) * 100);
      }

      // If there are conflicts, show them to the user
      if (conflictsList.length > 0) {
        setConflicts(conflictsList);
        setPendingUpdates({ updates, addedCount, skippedCount, data });
        setMessage(`${conflictsList.length} conflict(s) detected. Please choose how to handle each conflict.`);
        setIsUploading(false);
        setProgress(0);
        return;
      }

      // No conflicts, proceed with upload
      await dbSet(ref(dbRealtime, 'gl_mappings'), updates);
      await invalidateCache();

      if (skippedCount > 0) {
        setMessage(`Successfully uploaded ${addedCount} new mappings to Firebase. Skipped ${skippedCount} existing mappings.`);
      } else {
        setMessage(`Successfully uploaded ${addedCount} mappings to Firebase.`);
      }

      setLocalMappings([]);
      setConsolidated(null);
      setAnalysis(null);
      setPastedData('');
      setConflicts([]);
      setPendingUpdates(null);
    } catch (error) {
      console.error(error);
      if (error.code === 'PERMISSION_DENIED') {
        // Check if user is admin in Firestore but not in Realtime Database
        if (userDoc && userDoc.admin && user) {
          setMessage(
            `Permission denied. You are an admin in Firestore, but your UID has not been added to the Realtime Database admins node.\n\n` +
            `Please do one of the following:\n` +
            `1. Manually add in Firebase Console: Go to Realtime Database, add value "true" at path "admins/${user.uid}"\n` +
            `2. Or modify Firebase rules (recommended): Update the admins node write rules to allow users to write their own UID\n\n` +
            `Your UID: ${user.uid}`
          );
        } else {
          setMessage('Permission denied. You need admin privileges to upload data to Firebase. Please contact an administrator.');
        }
      } else {
        setMessage('Error uploading to Firebase: ' + error.message);
      }
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleResolveConflict = async (conflictIndex, choice) => {
    // choice: 'keep' (keep existing) or 'update' (update with new)
    const conflict = conflicts[conflictIndex];
    const { updates, addedCount, skippedCount, data } = pendingUpdates;

    let newSkippedCount = skippedCount;

    if (choice === 'update') {
      // Update with new GL Account
      updates[conflict.sanitizedKey] = {
        description: conflict.keyword,
        glAccount: conflict.newGlAccount
      };
    } else {
      // Keep existing, count as skipped
      newSkippedCount = skippedCount + 1;
    }

    // Remove this conflict from the list
    const newConflicts = conflicts.filter((_, idx) => idx !== conflictIndex);
    setConflicts(newConflicts);

    // Update pendingUpdates with new skippedCount
    setPendingUpdates({ ...pendingUpdates, skippedCount: newSkippedCount });

    // If all conflicts are resolved, proceed with upload
    if (newConflicts.length === 0) {
      setIsUploading(true);
      setMessage('Uploading to Firebase...');
      try {
        await dbSet(ref(dbRealtime, 'gl_mappings'), updates);
        await invalidateCache();

        if (newSkippedCount > 0) {
          setMessage(`Successfully uploaded ${addedCount} new mappings to Firebase. Skipped ${newSkippedCount} existing mappings.`);
        } else {
          setMessage(`Successfully uploaded ${addedCount} mappings to Firebase.`);
        }

        setLocalMappings([]);
        setConsolidated(null);
        setAnalysis(null);
        setPastedData('');
        setConflicts([]);
        setPendingUpdates(null);
      } catch (error) {
        console.error(error);
        if (error.code === 'PERMISSION_DENIED') {
          if (userDoc && userDoc.admin && user) {
            setMessage(
              `Permission denied. You are an admin in Firestore, but your UID has not been added to the Realtime Database admins node.\n\n` +
              `Please do one of the following:\n` +
              `1. Manually add in Firebase Console: Go to Realtime Database, add value "true" at path "admins/${user.uid}"\n` +
              `2. Or modify Firebase rules (recommended): Update the admins node write rules to allow users to write their own UID\n\n` +
              `Your UID: ${user.uid}`
            );
          } else {
            setMessage('Permission denied. You need admin privileges to upload data to Firebase.');
          }
        } else {
          setMessage('Error uploading to Firebase: ' + error.message);
        }
      } finally {
        setIsUploading(false);
        setProgress(0);
      }
    } else {
      setMessage(`${newConflicts.length} conflict(s) remaining to resolve.`);
    }
  };

  const handleResolveAllConflicts = (choice) => {
    // choice: 'keep' (keep all existing) or 'update' (update all with new)
    const { updates, addedCount, skippedCount } = pendingUpdates;

    let newSkippedCount = skippedCount;

    if (choice === 'update') {
      conflicts.forEach(conflict => {
        updates[conflict.sanitizedKey] = {
          description: conflict.keyword,
          glAccount: conflict.newGlAccount
        };
      });
    } else {
      // Keep all existing, add conflicts to skipped count
      newSkippedCount = skippedCount + conflicts.length;
    }

    // Proceed with upload
    setIsUploading(true);
    setMessage('Uploading to Firebase...');
    setConflicts([]);

    dbSet(ref(dbRealtime, 'gl_mappings'), updates)
      .then(() => invalidateCache())
      .then(() => {
        if (choice === 'keep') {
          setMessage(`Successfully uploaded ${addedCount} new mappings to Firebase. Skipped ${newSkippedCount} existing mappings (including ${conflicts.length} conflicts).`);
        } else {
          setMessage(`Successfully uploaded ${addedCount} new mappings and updated ${conflicts.length} existing mappings to Firebase.`);
        }

        setLocalMappings([]);
        setConsolidated(null);
        setAnalysis(null);
        setPastedData('');
        setPendingUpdates(null);
      })
      .catch((error) => {
        console.error(error);
        if (error.code === 'PERMISSION_DENIED') {
          if (userDoc && userDoc.admin && user) {
            setMessage(
              `Permission denied. You are an admin in Firestore, but your UID has not been added to the Realtime Database admins node.\n\n` +
              `Please do one of the following:\n` +
              `1. Manually add in Firebase Console: Go to Realtime Database, add value "true" at path "admins/${user.uid}"\n` +
              `2. Or modify Firebase rules (recommended): Update the admins node write rules to allow users to write their own UID\n\n` +
              `Your UID: ${user.uid}`
            );
          } else {
            setMessage('Permission denied. You need admin privileges to upload data to Firebase.');
          }
        } else {
          setMessage('Error uploading to Firebase: ' + error.message);
        }
      })
      .finally(() => {
        setIsUploading(false);
        setProgress(0);
      });
  };

  // Drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const rows = parseCSV(text);
        const mappings = rows.map(row => ({ description: row[0] || '', glAccount: row[1] || '' }));
        setLocalMappings(mappings);
        setPastedData(rows.map(row => `${row[0] || ''}\t${row[1] || ''}`).join('\n'));
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{
          fontWeight: 700,
          fontSize: '2rem',
          color: '#fff',
          margin: 0,
          letterSpacing: '0.01em',
        }}>
          Add GL Mappings
        </h2>
        <div style={{
          fontSize: '1rem',
          color: '#fff',
          marginTop: '0.25rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
        }}>
          Drag and drop a CSV file or paste tab-separated data below to train the classifier.
        </div>
      </div>

      <div className="card">
        {/* Removed 'Paste Data Here' heading for a cleaner UI */}
        <div style={{
          background: '#f0f4ff',
          border: '2px dashed #667eea',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
        }}>
          <span style={{ fontSize: '2rem', color: '#667eea', marginTop: '0.25rem' }}>📄</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '0.25rem' }}>
              Drag and drop a CSV file here, or paste tab-separated data below.
            </div>
            <div style={{ color: '#555', fontSize: '13px', marginBottom: '0.5rem' }}>
              Example:<br />
              <span style={{ fontFamily: 'monospace', color: '#222' }}>
                Descriptions	GL Account<br />
                UFT &amp; UFT WF EMP/PR PA 1099-R	Welfare UFT<br />
                Over CT Debit	Auto - Downpayment - 2024 Honda Civic
              </span>
            </div>
            <textarea
              ref={textareaRef}
              className="form-control textarea"
              value={pastedData}
              onChange={handlePaste}
              placeholder={'Paste tab-separated data here...'}
              style={{
                minHeight: '40vh',
                fontFamily: 'monospace',
                fontSize: '14px',
                border: isDragActive ? '2px solid #667eea' : undefined,
                background: isDragActive ? '#eaf0ff' : undefined,
                width: '100%',
                marginTop: 0
              }}
              disabled={isUploading}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          {analysis && <>
            <button className="btn btn-secondary" onClick={handleConsolidate} disabled={isUploading}>Perform Consolidation</button>
            <button className="btn" onClick={handleDownloadCSV} disabled={isUploading}>Download as CSV</button>
          </>}
          <button
            onClick={handleUploadToFirebase}
            disabled={isUploading || (!consolidated && !localMappings.length)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '16px',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.08)',
              cursor: isUploading || (!consolidated && !localMappings.length) ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
              outline: 'none',
              marginLeft: 'auto',
            }}
          >
            Add to Firebase Database
          </button>
          <button
            onClick={() => { setLocalMappings([]); setConsolidated(null); setAnalysis(null); setPastedData(''); }}
            disabled={isUploading}
            style={{
              background: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s, color 0.2s',
              outline: 'none',
            }}
            onMouseOver={e => { if (!isUploading) e.currentTarget.style.background = '#f0f4ff'; }}
            onMouseOut={e => { if (!isUploading) e.currentTarget.style.background = 'white'; }}
          >
            Clear
          </button>
        </div>
        {message === 'Consolidation complete! You can now download as CSV or add to Firebase.' && (
          <div className="alert alert-success" style={{ marginTop: '1rem', marginBottom: '1rem' }}>{message}</div>
        )}

        {conflicts.length > 0 && (
          <div className="card" style={{ marginTop: '2rem', borderLeft: '4px solid #ffc107', background: '#fff3cd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#856404', margin: 0 }}>Conflicts Detected ({conflicts.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleResolveAllConflicts('keep')}
                  style={{ fontSize: '14px', padding: '0.5rem 1rem' }}
                >
                  Keep All Existing
                </button>
                <button
                  className="btn"
                  onClick={() => handleResolveAllConflicts('update')}
                  style={{ fontSize: '14px', padding: '0.5rem 1rem', background: '#28a745', color: 'white' }}
                >
                  Update All to New Values
                </button>
              </div>
            </div>
            <p style={{ color: '#856404', marginBottom: '1rem' }}>
              The following keywords already exist in the database but with different GL Accounts. Please choose to keep the existing or update to the new value.
            </p>
            {conflicts.map((conflict, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                marginTop: '1rem',
                borderRadius: '4px',
                border: '2px solid #ffc107',
                background: 'white',
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong style={{ color: '#333', fontSize: '16px' }}>Keyword:</strong>
                  <div style={{
                    fontFamily: 'monospace',
                    background: '#f8f9fa',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    marginTop: '0.25rem',
                    color: '#333'
                  }}>
                    {conflict.keyword}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#856404' }}>Existing GL Account:</strong>
                    <div style={{
                      fontFamily: 'monospace',
                      background: '#fff3cd',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      marginTop: '0.25rem',
                      color: '#856404',
                      border: '1px solid #ffc107'
                    }}>
                      {conflict.existingGlAccount}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#28a745' }}>New GL Account:</strong>
                    <div style={{
                      fontFamily: 'monospace',
                      background: '#d4edda',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      marginTop: '0.25rem',
                      color: '#155724',
                      border: '1px solid #28a745'
                    }}>
                      {conflict.newGlAccount}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleResolveConflict(idx, 'keep')}
                    style={{ flex: 1 }}
                  >
                    Keep Existing
                  </button>
                  <button
                    className="btn"
                    onClick={() => handleResolveConflict(idx, 'update')}
                    style={{ flex: 1, background: '#28a745', color: 'white' }}
                  >
                    Update to New Value
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {isUploading && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#333', marginBottom: '0.25rem' }}>
              <span>Processing...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div style={{ width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${progress}%`,
                height: '8px',
                backgroundColor: '#667eea',
                transition: 'width 0.1s linear'
              }}></div>
            </div>
          </div>
        )}

        {analysis && (
          <div className="card" style={{ marginTop: '2rem', borderLeft: '4px solid #667eea' }}>
            <h3>Analysis Report</h3>
            {analysis.duplicatesRemoved > 0 && <p>Removed {analysis.duplicatesRemoved} duplicate keywords.</p>}
            {analysis.results.length === 0 && <p>No potential optimizations found.</p>}
            {analysis.results.map((result, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                marginTop: '1rem',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: result.status === 'OK' ? '#c3e6cb' : '#f5c6cb',
                background: result.status === 'OK' ? '#d4edda' : '#f8d7da',
              }}>
                <p><strong>Proposed Keyword:</strong> "{result.keyword}" for GL Account "<strong>{result.glAccount}</strong>"</p>
                <p>This would replace {result.count} existing keywords.</p>
                {result.status === 'CONFLICT' && result.conflict && (
                  <div style={{ color: '#721c24', marginTop: '0.5rem' }}>
                    <strong>Conflict:</strong> This action is blocked because the keyword "{result.keyword}" was also found in "<em>{result.conflict.description}</em>", which is linked to a different GL Account: "<strong>{result.conflict.glAccount}</strong>".
                  </div>
                )}
                {result.status === 'OK' && (
                  <p style={{ color: '#155724', marginTop: '0.5rem' }}>
                    <strong>Status:</strong> This consolidation is safe and ready to be performed.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadData; 